'use client';

import React, { useState, useEffect } from 'react';
import { ProgressBar } from '../atomic/ProgressBar';
import ComparisonBarMobile from './ComparisonBarMobile';

/**
 * [DC01] Molecular component that displays a comparison bar for communities
 */
interface ComparisonBarProps {
  name: string;
  value: number;
  maxValue: number;
  isSelected?: boolean;
  className?: string;
  compact?: boolean;
}

// Named export for better tree-shaking and explicit imports
export function ComparisonBar({
  name,
  value,
  maxValue,
  isSelected = false,
  className = '',
  compact = false
}: ComparisonBarProps) {
  const [isMobile, setIsMobile] = useState<boolean | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // [CP01] Simple calculation with minimal logic
  const percentage = Math.min(100, (value / maxValue) * 100);
  
  if (isMobile === null) return null;
  if (isMobile) {
    return (
      <ComparisonBarMobile
        title={name}
        value={value}
        maxValue={maxValue}
        unit="€"
        color={isSelected ? '#16A34A' : '#3B82F6'}
        showPercentage={true}
      />
    );
  }
  
  // [NS04] Conditional classes for selected state
  const labelClasses = isSelected 
    ? 'font-bold text-green-700 dark:text-green-500' 
    : 'text-gray-700 dark:text-gray-300';
  
  const barColor = isSelected ? 'bg-green-600' : 'bg-blue-500';
  
  return (
    <div className={`${compact ? 'mb-2' : 'mb-3'} ${className}`}>
      <div className="flex justify-between mb-0.5">
        <span className={`${compact ? 'text-xs' : 'text-sm'} ${labelClasses}`}>
          {name}:
        </span>
        <span className={`${compact ? 'text-xs' : 'text-sm'} ${labelClasses}`}>
          €{value.toLocaleString('de-AT')}
        </span>
      </div>
      <ProgressBar 
        value={percentage} 
        color={barColor}
        className={compact ? 'h-1.5' : ''}
        animated={!compact} 
      />
    </div>
  );
}
